import React, { useState } from 'react';
import { SpendingSumModel } from 'src/types/spending-sum.model';
import styles from './spendingSumRows.module.scss';
import FormatService from 'src/services/format.service';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import { ButtonTertiary, IconDown, IconRight } from '@americanexpress/dls-react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Selectors from 'src/utils/selectors';
import CategoryHistoryModal from 'src/components/atomic/organisms/transaction/category-history-modal/CategoryHistoryModal';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { CategoryModel } from 'src/types/category.model';
import { DLSSize } from 'src/types/dls-size.enum';
import { useHistory, useLocation } from 'react-router';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { getCategoryTransactionsUrl, goToBudgetCategoryTransactions } from 'src/services/router.service';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import { DLSBreakpoints } from 'src/types/dlsBreakpoints.enum';
import { getCategoryTagName, getIcon } from 'src/utils/category.utils';

const outerCircleDiameterLg: string = '48px';
const innerCircleDiameter: string = '12px';

const icons: any = {
    Charity: {
        className: styles.charity
    },
    Education: {
        className: styles.education
    },
    Family: {
        className: styles.family
    },
    Financial: {
        className: styles.financial
    },
    'Food & Dining': {
        className: styles.food
    },
    'Fun & Entertainment': {
        className: styles.entertainment
    },
    'Health & Wellness': {
        className: styles.health
    },
    Home: {
        className: styles.home
    },
    Services: {
        className: styles.services
    },
    Shopping: {
        className: styles.shopping
    },
    'Travel & Transportation': {
        className: styles.travel
    },
    'Needs Categorization': {
        className: styles.uncategorized
    },
    Miscellaneous: {
        className: styles.miscellaneous
    }
};

// internal fragment for shared elements between parent and child rows
const SpendingSumRowFragment = ({
    spendingSum,
    categories
}: {
    spendingSum: SpendingSumModel;
    categories: CategoryModel[];
}): JSX.Element => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const history = useHistory();
    const isHomePage = history.location.pathname === BudgetPages.HOME;

    const category: CategoryModel = categories?.find((category) => category.id === spendingSum.groupId);
    const fullTagName: string =
        adobeTags.CategoryBreakdown.CategorySelect + '>' + getCategoryTagName(category, categories);
    const percentSpent: string = LiteralsService.getLiteral('SpendingCategories.ofTotal', [
        spendingSum.percentSpent * 100 >= 1 ? FormatService.formatPercent(spendingSum.percentSpent * 100, 0) : '<1%'
    ]);
    const spent: string = FormatService.formatCurrency(spendingSum.spent);
    const averageSpent: string = LiteralsService.getLiteral('SpendingCategories.avgMonth', [
        FormatService.formatCurrency(spendingSum.average, { round: true })
    ]);
    const openModal = (): void => setModalOpen(true);
    const closeModal = (): void => setModalOpen(false);

    return (
        <>
            <div>
                <Typography variant={TypographyVariants.LINK_2}>
                    <Anchor
                        onClick={() => {
                            if (isHomePage) goToBudgetCategoryTransactions(false, history, spendingSum.groupId);
                            else openModal();
                        }}
                        tag={fullTagName}
                        href={isHomePage ? getCategoryTransactionsUrl(spendingSum.groupId) : undefined}
                        data-cy={Selectors.SpendSmart.SPENDING_CATEGORY_HISTORY}
                        aria-label={
                            isHomePage
                                ? LiteralsService.getLiteral('Budget.Current.viewTransactionsLinkDescription', [name])
                                : LiteralsService.getLiteral('Budget.History.viewTransactionsLinkDescription', [name])
                        }
                    >
                        {category.name}
                    </Anchor>
                </Typography>

                <Typography
                    component="p"
                    variant={TypographyVariants.SUBTEXT}
                    data-cy={Selectors.SpendSmart.SPENDING_SUM_ROW_PERCENT}
                >
                    {percentSpent}
                </Typography>
            </div>
            <div className={styles.lastColumn}>
                <Typography
                    component="p"
                    variant={TypographyVariants.BODY_BOLD}
                    data-cy={Selectors.SpendSmart.SPENDING_SUM_ROW_SPENT}
                >
                    {spent}
                </Typography>
                <Typography
                    component="p"
                    variant={TypographyVariants.LEGAL_PRIMARY}
                    data-cy={Selectors.SpendSmart.SPENDING_SUM_ROW_AVERAGE}
                >
                    {averageSpent}
                </Typography>
            </div>
            {modalOpen && <CategoryHistoryModal groupId={spendingSum.groupId} closeModal={closeModal} />}
        </>
    );
};

export const SpendingSumParentRow = ({
    spendingSum,
    categories,
    toggleOpen,
    open,
    disabled,
    noChildrenInGrid,
    topBorder
}: {
    spendingSum: SpendingSumModel;
    categories: CategoryModel[];
    open: boolean;
    toggleOpen: () => void;
    disabled?: boolean;
    noChildrenInGrid?: boolean;
    topBorder?: boolean;
}): JSX.Element => {
    {
        /* Custom breakpoint for design */
    }
    const { isCustomMinWidth: isMinWidthDLSSmall } = useBreakpoints(DLSBreakpoints.SMALL);
    const { pathname } = useLocation();

    const name = categories?.find((category) => category.id === spendingSum.groupId).name;

    return (
        <div
            className={`${styles.row} ${topBorder && styles.parentRowBorder}`}
            data-cy={Selectors.SpendSmart.SPENDING_SUM_PARENT_ROW}
        >
            <div className={styles.iconContainer}>
                {noChildrenInGrid ? (
                    <div className={styles.noChildrenPlaceholder}></div>
                ) : disabled ? (
                    <div className={styles.parentButtonPlaceholder}></div>
                ) : (
                    <h3>
                        <ButtonTertiary
                            icon={
                                open ? <IconDown title={`Collapse ${name}`} /> : <IconRight title={`Expand ${name}`} />
                            }
                            onClick={() => {
                                toggleOpen();
                                trackClick(
                                    pathname,
                                    `${
                                        open ? adobeTags.CategoryBreakdown.Close : adobeTags.CategoryBreakdown.Expand
                                    }>${FormatService.formatTag(name)}`
                                );
                            }}
                            size={DLSSize.SMALL}
                            data-cy={Selectors.SpendSmart.SPENDING_SUM_EXPAND_BUTTON}
                            aria-expanded={open}
                            aria-controls={`spending-sum-children-${spendingSum.groupId}`}
                            id={`spending-sum-parent-button-${spendingSum.groupId}`}
                        />
                    </h3>
                )}
                {isMinWidthDLSSmall && (
                    <Circle className={`${styles.circle} ${icons[name]?.className}`} diameter={outerCircleDiameterLg}>
                        {React.createElement(getIcon(name))}
                    </Circle>
                )}
            </div>

            <SpendingSumRowFragment spendingSum={spendingSum} categories={categories} />
        </div>
    );
};
export const SpendingSumChildRow = ({
    spendingSum,
    spendingSumParentName,
    categories,
    darkTopBorder
}: {
    spendingSum: SpendingSumModel;
    spendingSumParentName: string;
    categories: CategoryModel[];
    darkTopBorder?: boolean;
}): JSX.Element => {
    return (
        <li
            className={`${styles.row} ${styles.childRow} ${darkTopBorder && styles.darkTopBorder}`}
            data-cy={Selectors.SpendSmart.SPENDING_SUM_CHILD_ROW}
        >
            <div className={styles.iconContainer}>
                <div className={styles.childButtonPlaceholder}></div>
                <div className={styles.smallCircleContainer}>
                    <Circle className={icons[spendingSumParentName]?.className} diameter={innerCircleDiameter} />
                </div>
            </div>
            <SpendingSumRowFragment spendingSum={spendingSum} categories={categories} />
        </li>
    );
};
